import { openCart, updateCart } from '@theme/cart-actions';
// Product page upsell. Each [data-upsell-item] carries its variants as JSON; swatch buttons and
// selects ([data-upsell-option="position"]) pick option values. The matching variant updates the
// item's image, links, price (compare-at when on sale), availability and the Add button's
// data-variant-id. Values with no available variant (given the other selections) are marked
// data-unavailable. The --add command adds the selected variant to the cart and opens it.
class ProductUpsell extends HTMLElement {
  #controller = null;
  #money = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#money = new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    this.querySelectorAll('[data-upsell-item]').forEach((item) => {
      try {
        item.variants = JSON.parse(item.querySelector('[data-upsell-variants]')?.textContent || '[]');
      } catch {
        item.variants = [];
      }
      this.#update(item, false);
    });

    this.addEventListener('click', (event) => {
      const swatch = event.target.closest('button[data-upsell-option]');
      if (!swatch) return;
      const group = swatch.parentElement;
      group.querySelectorAll('button[data-upsell-option]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button === swatch));
      });
      this.#update(swatch.closest('[data-upsell-item]'), true);
    }, { signal });

    this.addEventListener('change', (event) => {
      if (event.target.matches('select[data-upsell-option]')) this.#update(event.target.closest('[data-upsell-item]'), true);
    }, { signal });

    this.addEventListener('command', (event) => {
      if (event.command === '--add' && event.source) this.#add(event.source);
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #selection(item) {
    const selected = [];
    item.querySelectorAll('select[data-upsell-option]').forEach((select) => {
      selected[Number(select.dataset.upsellOption) - 1] = select.value;
    });
    item.querySelectorAll('button[data-upsell-option][aria-pressed="true"]').forEach((button) => {
      selected[Number(button.dataset.upsellOption) - 1] = button.dataset.value;
    });
    return selected;
  }

  #match(item, selected) {
    return item.variants.find((variant) =>
      variant.options.every((value, index) => selected[index] === undefined || selected[index] === value)
    );
  }

  #format(cents) {
    return this.#money.format(cents / 100);
  }

  #update(item, changed) {
    if (!item || !item.variants.length) return;
    const selected = this.#selection(item);
    const variant = this.#match(item, selected);

    // Availability hints: a value is unavailable when no available variant has it with the other selections.
    item.querySelectorAll('[data-upsell-option]').forEach((control) => {
      const index = Number(control.dataset.upsellOption) - 1;
      const check = (value) => item.variants.some((candidate) => candidate.available && candidate.options.every(
        (optionValue, optionIndex) => (optionIndex === index ? optionValue === value : selected[optionIndex] === undefined || selected[optionIndex] === optionValue)
      ));
      if (control.tagName === 'BUTTON') control.toggleAttribute('data-unavailable', !check(control.dataset.value));
      else [...control.options].forEach((option) => { option.dataset.unavailable = String(!check(option.value)); });
    });
    item.querySelectorAll('[data-upsell-selected]').forEach((label) => {
      label.textContent = selected[Number(label.dataset.upsellSelected) - 1] || '';
    });

    const button = item.querySelector('[command="--add"]');
    const label = item.querySelector('[data-upsell-label]');
    button.removeAttribute('data-added');

    if (!variant) {
      button.setAttribute('disabled', '');
      label.textContent = this.dataset.labelUnavailable;
      return;
    }

    button.dataset.variantId = variant.id;
    button.toggleAttribute('disabled', !variant.available);
    label.textContent = variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut;

    if (!changed) return;

    const image = item.querySelector('[data-upsell-image]') || item.querySelector('[data-upsell-link] img');
    if (image && variant.image) {
      image.removeAttribute('srcset');
      image.src = variant.image;
    }
    item.querySelectorAll('[data-upsell-link]').forEach((link) => { link.href = variant.url; });

    const price = item.querySelector('[data-upsell-price]');
    if (price) {
      const onSale = variant.compare > variant.price;
      const wrap = document.createElement('div');
      wrap.className = 'flex flex-col items-end text-sm font-semibold';
      const current = document.createElement('span');
      current.textContent = this.#format(variant.price);
      if (onSale) current.className = 'text-accent';
      wrap.append(current);
      if (onSale) {
        const compare = document.createElement('s');
        compare.className = 'text-xs font-normal text-on-surface/50';
        compare.textContent = this.#format(variant.compare);
        wrap.append(compare);
      }
      price.replaceChildren(wrap);
    }
  }

  async #add(button) {
    const id = button.dataset.variantId;
    if (!id || button.hasAttribute('disabled') || button.getAttribute('aria-busy') === 'true') return;
    const item = button.closest('[data-upsell-item]');
    const error = item?.querySelector('[data-upsell-error]');
    if (error) error.hidden = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const result = await updateCart(
        { lines: [{ merchandiseId: id, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-product-upsell' } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) throw new Error(message);
      this.#added(button);
      await openCart();
    } catch (failure) {
      if (error) {
        error.textContent = failure?.message || this.dataset.labelError;
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  #added(button) {
    button.setAttribute('data-added', '');
    const label = button.querySelector('[data-upsell-label]');
    if (label) label.textContent = this.dataset.labelAdded;
    const live = this.querySelector('[data-upsell-live]');
    if (live) live.textContent = this.dataset.labelAdded;
    setTimeout(() => {
      button.removeAttribute('data-added');
      if (label && !button.hasAttribute('disabled')) label.textContent = this.dataset.labelAdd;
    }, 2400);
  }
}

if (!customElements.get('s-product-upsell')) customElements.define('s-product-upsell', ProductUpsell);

